// pages/CI/SLDEditor.js
//
// CI-admin SLD authoring tool — port of the Mideast MESites diagram editor,
// pointed at the MAIN commercial-sld module (/api/commercial-sld/*) and the main
// auth token. Flow: pick Client → pick Site → upload diagram → place pins
// (nodes) and draw strings (polygons). This seeds what the merged field app
// captures against and the CI viewer renders.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Plus, X, Upload, Trash2, CheckCircle2, Move, BoxSelect, Layers, MapPin,
} from 'lucide-react';
import axios from 'axios';

const BASE = process.env.REACT_APP_API_BASE_URL;
const IMG_BASE = BASE ? BASE.replace('/api', '') : '';
const authHdr = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});
const multipartHdr = () => ({
  headers: {
    Authorization: `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'multipart/form-data',
  },
});

const STRING_COLORS = [
  '#3B82F6', '#22D3A5', '#F59E0B', '#EF4444',
  '#A855F7', '#EC4899', '#F97316', '#14B8A6',
];

const centroid = (pts) => ({
  x: pts.reduce((s, p) => s + Number(p.x), 0) / pts.length,
  y: pts.reduce((s, p) => s + Number(p.y), 0) / pts.length,
});
const svgPts = (pts) => pts.map((p) => `${Number(p.x)},${Number(p.y)}`).join(' ');

// ─── THEME STYLES (AGGRESSIVE GLOBAL DARK MODE + ORIGINAL COLORS) ───────────
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800;900&family=DM+Mono:wght@400;500&display=swap');

  /* Fallback / Light Mode Variables */
  :root, body, html, .csld-wrapper {
    --bg-page: #F3F4F6;
    --bg-card: #ffffff;
    --bg-panel: #F9FAFB;
    --bg-panel-alt: #F3F4F6;
    --text-strong: #111827;
    --text-main: #374151;
    --text-muted: #6B7280;
    --border-main: #E5E7EB;
    --shadow-sm: 0 1px 3px rgba(0,0,0,0.05);

    --accent-blue: #2563EB;
    --accent-blue-hover: #1D4ED8;
    --accent-blue-light: rgba(37, 99, 235, 0.1);
    
    --accent-red: #DC2626;
    --accent-red-hover: #B91C1C;
    --accent-red-light: rgba(220, 38, 38, 0.1);

    --btn-ghost-bg: rgba(0, 0, 0, 0.05);
    --btn-ghost-hover: rgba(0, 0, 0, 0.08);
    --btn-ghost-text: #4B5563;

    --skeleton-grad: linear-gradient(90deg, #F3F4F6, #E5E7EB, #F3F4F6);
    color-scheme: light;
  }

  /* Extreme Aggressive Dark Mode Selectors (Using original deep navy colors) */
  html.dark, html.dark-mode, html[data-theme='dark'], html[data-bs-theme='dark'],
  body.dark, body.dark-mode, body[data-theme='dark'], body[data-bs-theme='dark'],
  .dark, .dark-mode, [data-theme='dark'], [data-bs-theme='dark'],
  .dark .csld-wrapper, .dark-mode .csld-wrapper, [data-theme='dark'] .csld-wrapper, [data-bs-theme='dark'] .csld-wrapper,
  .csld-wrapper.dark, .csld-wrapper[data-theme='dark'] {
    --bg-page: #050811 !important;
    --bg-card: #0D1120 !important;
    --bg-panel: #161B2E !important;
    --bg-panel-alt: #1E2535 !important;
    --text-strong: #ffffff !important;
    --text-main: #E2E8F0 !important;
    --text-muted: rgba(255,255,255,0.4) !important;
    --border-main: rgba(255,255,255,0.1) !important;
    --shadow-sm: 0 4px 20px rgba(0,0,0,0.4) !important;

    --accent-blue: #38BDF8 !important;
    --accent-blue-hover: #0EA5E9 !important;
    --accent-blue-light: rgba(56, 189, 248, 0.1) !important;

    --accent-red: #F87171 !important;
    --accent-red-hover: #EF4444 !important;
    --accent-red-light: rgba(239, 68, 68, 0.15) !important;

    --btn-ghost-bg: rgba(255, 255, 255, 0.05) !important;
    --btn-ghost-hover: rgba(255, 255, 255, 0.09) !important;
    --btn-ghost-text: #E2E8F0 !important;

    --skeleton-grad: linear-gradient(90deg, rgba(255,255,255,0.04), rgba(255,255,255,0.08), rgba(255,255,255,0.04)) !important;
    
    /* Fix for native dropdowns in dark mode */
    color-scheme: dark !important; 
  }

  .csld-wrapper {
    background-color: var(--bg-page);
    color: var(--text-main);
    font-family: 'DM Sans', sans-serif;
    min-height: 100vh;
    padding: 24px;
    transition: background-color 0.3s ease, color 0.3s ease;
  }

  .csld-wrapper * {
    box-sizing: border-box;
    transition: background-color 0.3s ease, border-color 0.3s ease, color 0.3s ease;
  }

  .md-page-title { font-size: 24px; font-weight: 800; color: var(--text-strong); }
  .md-page-sub   { font-size: 13px; color: var(--text-muted); margin-top: 4px; font-family: 'DM Mono', monospace; }

  .md-btn {
    display: inline-flex; align-items: center; gap: 6px; padding: 8px 14px;
    border-radius: 9px; font-size: 13px; font-weight: 700; font-family: 'DM Sans', sans-serif;
    cursor: pointer; border: 1px solid transparent; transition: all 0.15s;
  }
  .md-btn:disabled { opacity: 0.55; cursor: not-allowed; }
  .md-btn-primary { background: var(--accent-blue); color: #ffffff; }
  .md-btn-primary:hover:not(:disabled) { background: var(--accent-blue-hover); }
  .md-btn-ghost { background: var(--btn-ghost-bg); color: var(--btn-ghost-text); border-color: var(--border-main); }
  .md-btn-ghost:hover:not(:disabled) { background: var(--btn-ghost-hover); }
  .md-btn-danger { background: var(--accent-red-light); color: var(--accent-red); border-color: var(--accent-red-light); }
  .md-btn-danger:hover:not(:disabled) { background: var(--accent-red); color: #fff; }

  .md-spinner {
    display: inline-block; width: 13px; height: 13px;
    border: 2px solid var(--text-muted); border-top-color: var(--accent-blue);
    border-radius: 50%; animation: md-spin 0.7s linear infinite;
  }
  @keyframes md-spin { to { transform: rotate(360deg); } }
  .md-skeleton {
    background: var(--skeleton-grad);
    background-size: 200% 100%; animation: md-shimmer 1.3s infinite; border-radius: 12px;
  }
  @keyframes md-shimmer { to { background-position: -200% 0; } }
  
  .md-empty { text-align: center; color: var(--text-muted); font-size: 14px; padding: 48px 0; font-weight: 500; }
  .md-empty-icon {
    width: 56px; height: 56px; border-radius: 14px; background: var(--bg-panel);
    display: flex; align-items: center; justify-content: center; margin: 0 auto 16px; color: var(--text-muted);
    border: 1px dashed var(--border-main);
  }

  .csld-card {
    background: var(--bg-card); border: 1px solid var(--border-main);
    border-radius: 16px; padding: 24px; margin-bottom: 20px;
    box-shadow: var(--shadow-sm);
  }
  .csld-pickers { display: flex; gap: 16px; flex-wrap: wrap; }
  .csld-field { display: flex; flex-direction: column; gap: 8px; min-width: 260px; flex: 1; }
  .csld-label {
    font-size: 11px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase;
    color: var(--text-muted); font-family: 'DM Mono', monospace;
  }

  /* DROPDOWN FIXES FOR DARK MODE */
  .csld-select {
    padding: 12px 14px; background-color: var(--bg-panel);
    border: 1px solid var(--border-main); border-radius: 10px;
    color: var(--text-strong); font-size: 14px; font-family: 'DM Sans', sans-serif; outline: none; cursor: pointer;
    font-weight: 600;
  }
  .csld-select:focus { border-color: var(--accent-blue); }
  .csld-select option {
    background-color: var(--bg-panel-alt);
    color: var(--text-strong);
  }

  /* Diagram upload zone */
  .ms-diagram-upload-zone {
    border: 2px dashed var(--accent-blue); border-radius: 14px; padding: 48px 24px;
    text-align: center; cursor: pointer; transition: all 0.2s; background: var(--accent-blue-light);
  }
  .ms-diagram-upload-zone:hover { border-color: var(--accent-blue-hover); opacity: 0.9; }
  .ms-diagram-upload-icon {
    width: 56px; height: 56px; border-radius: 14px; background: var(--bg-card);
    display: flex; align-items: center; justify-content: center; margin: 0 auto 16px; color: var(--accent-blue);
    box-shadow: var(--shadow-sm);
  }
  .ms-diagram-upload-title { font-size: 16px; font-weight: 700; color: var(--text-strong); margin-bottom: 6px; }
  .ms-diagram-upload-sub   { font-size: 13px; color: var(--text-muted); }

  .ms-mode-toggle { display: flex; gap: 8px; margin-bottom: 18px; }
  .ms-mode-btn {
    display: flex; align-items: center; gap: 6px; padding: 8px 16px; border-radius: 8px;
    font-size: 12px; font-weight: 700; font-family: 'DM Mono', monospace; cursor: pointer;
    border: 1px solid var(--border-main); background: var(--bg-panel);
    color: var(--text-muted); transition: all 0.18s;
  }
  .ms-mode-btn.active-pins    { background: rgba(245,158,11,0.12); color: #D97706; border-color: rgba(245,158,11,0.3); }
  .ms-mode-btn.active-strings { background: var(--accent-blue-light); color: var(--accent-blue); border-color: var(--accent-blue); }

  .ms-pin-canvas-wrap {
    position: relative; display: inline-block; width: 100%; border-radius: 12px; overflow: hidden;
    border: 2px solid var(--border-main); user-select: none; cursor: crosshair; background: var(--bg-panel);
  }
  .ms-pin-canvas-img { width: 100%; display: block; user-select: none; -webkit-user-drag: none; }
  .ms-svg-overlay { position: absolute; inset: 0; width: 100%; height: 100%; pointer-events: none; overflow: visible; }
  .ms-string-label-chip {
    position: absolute; transform: translate(-50%, -50%); font-size: 11px; font-weight: 800;
    font-family: 'DM Mono', monospace; border-radius: 6px; padding: 4px 8px; white-space: nowrap;
    background: var(--text-strong); color: var(--bg-page); pointer-events: none; z-index: 10;
    box-shadow: var(--shadow-sm);
  }
  .ms-draft-toolbar {
    display: flex; align-items: center; gap: 10px; padding: 12px 16px; border-radius: 10px; margin-bottom: 14px;
    background: var(--accent-blue-light); border: 1px solid var(--border-main);
    font-size: 12px; font-weight: 700; font-family: 'DM Mono', monospace; color: var(--accent-blue);
  }
  .ms-pin { position: absolute; transform: translate(-50%, -50%); z-index: 10; cursor: pointer; }
  .ms-pin-dot {
    width: 24px; height: 24px; border-radius: 50%; display: flex; align-items: center; justify-content: center;
    font-size: 10px; font-weight: 800; color: #fff; background: #F59E0B;
    box-shadow: 0 0 0 3px rgba(245,158,11,0.30), 0 4px 12px rgba(0,0,0,0.50);
    transition: transform 0.15s; font-family: 'DM Mono', monospace;
  }
  .ms-pin:hover .ms-pin-dot { transform: scale(1.15); }
  .ms-pin-tooltip {
    position: absolute; bottom: 32px; left: 50%; transform: translateX(-50%);
    background: var(--text-strong); border-radius: 8px; padding: 6px 12px;
    white-space: nowrap; font-size: 12px; color: var(--bg-page); font-weight: 700; pointer-events: none;
    opacity: 0; transition: opacity 0.15s; box-shadow: var(--shadow-sm); z-index: 20;
  }
  .ms-pin:hover .ms-pin-tooltip { opacity: 1; }
  .ms-pin-remove {
    position: absolute; top: -8px; right: -8px; width: 16px; height: 16px; border-radius: 50%;
    background: var(--accent-red); color: #fff; display: flex; align-items: center; justify-content: center;
    font-size: 10px; opacity: 0; transition: opacity 0.15s; cursor: pointer; border: none;
  }
  .ms-pin:hover .ms-pin-remove { opacity: 1; }
  
  .ms-pin-list, .ms-string-list { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 12px; margin-top: 16px; }
  .ms-pin-list-item, .ms-string-item {
    display: flex; align-items: center; gap: 12px; padding: 12px 16px; border-radius: 10px;
    background: var(--bg-panel); border: 1px solid var(--border-main);
  }
  .ms-pin-list-num {
    width: 24px; height: 24px; border-radius: 50%; background: #F59E0B; color: #fff;
    display: flex; align-items: center; justify-content: center; font-size: 10px; font-weight: 800;
    flex-shrink: 0; font-family: 'DM Mono', monospace;
  }
  .ms-pin-label-input {
    flex: 1; background: transparent; border: none; outline: none; color: var(--text-strong); font-size: 14px; font-weight: 600;
    border-bottom: 1px solid var(--border-main); padding: 4px 0;
  }
  .ms-pin-label-input:focus { border-bottom-color: var(--accent-blue); }
  .ms-string-color-block { width: 16px; height: 16px; border-radius: 4px; flex-shrink: 0; }
  
  .ms-pending-form {
    background: var(--accent-blue-light); border: 1px solid var(--border-main);
    border-radius: 12px; padding: 20px; margin-top: 16px;
  }
  .ms-pending-form-title {
    font-size: 11px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase;
    color: var(--accent-blue); font-family: 'DM Mono', monospace; margin-bottom: 14px;
  }
  .ms-color-row { display: flex; align-items: center; gap: 10px; margin-bottom: 16px; }
  .ms-color-swatch {
    width: 26px; height: 26px; border-radius: 50%; cursor: pointer; flex-shrink: 0;
    border: 2px solid transparent; transition: transform 0.15s, border-color 0.15s;
  }
  .ms-color-swatch:hover { transform: scale(1.15); }
  .ms-color-swatch.selected { border-color: var(--text-strong); transform: scale(1.1); box-shadow: var(--shadow-sm); }
  
  .ms-canvas-hint {
    font-size: 12px; color: var(--text-muted); font-family: 'DM Mono', monospace; font-weight: 600;
    margin: 12px 0 16px; display: flex; align-items: center; gap: 8px;
  }
  .csld-input {
    width: 100%; padding: 12px 14px; background: var(--bg-card);
    border: 1px solid var(--border-main); border-radius: 10px; color: var(--text-strong);
    font-size: 14px; font-family: 'DM Sans', sans-serif; outline: none; font-weight: 600;
  }
  .csld-input:focus { border-color: var(--accent-blue); }
`;

const CISLDEditor = () => {
  // Global Theme Observer
  const [isDark, setIsDark] = useState(() => {
    if (typeof document !== 'undefined') {
      return document.documentElement.classList.contains('dark') ||
             document.body.classList.contains('dark') ||
             document.documentElement.getAttribute('data-theme') === 'dark' ||
             document.body.getAttribute('data-theme') === 'dark';
    }
    return false;
  });

  useEffect(() => {
    const checkTheme = () => {
      setIsDark(
        document.documentElement.classList.contains('dark') ||
        document.body.classList.contains('dark') ||
        document.documentElement.getAttribute('data-theme') === 'dark' ||
        document.body.getAttribute('data-theme') === 'dark' ||
        document.body.classList.contains('dark-mode')
      );
    };

    checkTheme();
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class', 'data-theme'] });
    observer.observe(document.body, { attributes: true, attributeFilter: ['class', 'data-theme'] });
    return () => observer.disconnect();
  }, []);

  const [clients, setClients] = useState([]);
  const [sites, setSites] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [loadingSites, setLoadingSites] = useState(false);

  const [diagram, setDiagram] = useState(null);
  const [diagramLoading, setDiagramLoading] = useState(false);
  const [pins, setPins] = useState([]);
  const [strings, setStrings] = useState([]);
  const [savingPins, setSavingPins] = useState(false);
  const [uploadingDiag, setUploadingDiag] = useState(false);

  const [diagramMode, setDiagramMode] = useState('pins');
  const [draftPoints, setDraftPoints] = useState([]);
  const [mousePos, setMousePos] = useState(null);
  const [pendingShape, setPendingShape] = useState(null);
  const [pendingLabel, setPendingLabel] = useState('');
  const [pendingColor, setPendingColor] = useState(STRING_COLORS[0]);
  const [savingString, setSavingString] = useState(false);

  const diagramInputRef = useRef(null);
  const canvasRef = useRef(null);

  const selectedSite = sites.find((s) => String(s.id) === String(selectedSiteId)) || null;

  // ── Clients ───────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${BASE}/commercial-clients`);
        if (r.data?.success) setClients(r.data.data.clients || []);
      } catch (e) {
        console.error('[CI SLD] load clients error:', e);
      }
    })();
  }, []);

  // ── Sites for the chosen client ───────────────────────────────────
  useEffect(() => {
    if (!selectedClientId) { setSites([]); setSelectedSiteId(''); return; }
    (async () => {
      setLoadingSites(true);
      try {
        const r = await axios.get(`${BASE}/commercial-sites/${selectedClientId}`);
        setSites(Array.isArray(r.data) ? r.data : []);
      } catch (e) {
        console.error('[CI SLD] load sites error:', e);
        setSites([]);
      } finally {
        setLoadingSites(false);
      }
    })();
    setSelectedSiteId('');
    setDiagram(null);
    setPins([]);
    setStrings([]);
  }, [selectedClientId]);

  const resetDraft = () => { setDraftPoints([]); setMousePos(null); setPendingShape(null); setPendingLabel(''); setPendingColor(STRING_COLORS[0]); };

  // ── Diagram for the chosen site ───────────────────────────────────
  const loadDiagram = useCallback(async (siteId) => {
    setDiagramLoading(true);
    setDiagram(null); setPins([]); setStrings([]); setDiagramMode('pins'); resetDraft();
    try {
      const listRes = await axios.get(`${BASE}/commercial-sld/diagrams/site/${siteId}`, authHdr());
      const list = listRes.data?.diagrams || [];
      if (!list.length) { setDiagram(null); return; }
      const id = list[0].id;
      const [dRes, sRes] = await Promise.all([
        axios.get(`${BASE}/commercial-sld/diagrams/${id}`, authHdr()),
        axios.get(`${BASE}/commercial-sld/strings/diagram/${id}`, authHdr()).catch(() => ({ data: { strings: [] } })),
      ]);
      const d = dRes.data.diagram;
      setDiagram(d);
      setPins((d.points || []).map((p, i) => ({ ...p, _localId: p.id ?? `n${i}` })));
      setStrings(sRes.data.strings || []);
    } catch (e) {
      console.error('[CI SLD] load diagram error:', e);
    } finally {
      setDiagramLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedSiteId) loadDiagram(selectedSiteId);
  }, [selectedSiteId, loadDiagram]);

  // ── Canvas helpers ────────────────────────────────────────────────
  const getCursorPct = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return {
      x: parseFloat(((e.clientX - rect.left) / rect.width * 100).toFixed(2)),
      y: parseFloat(((e.clientY - rect.top) / rect.height * 100).toFixed(2)),
    };
  };

  const handleCanvasClick = (e) => {
    if (!canvasRef.current) return;
    const { x, y } = getCursorPct(e);
    if (diagramMode === 'pins') {
      setPins((prev) => [...prev, { _localId: Date.now(), label: '', x_percent: x, y_percent: y, order_index: prev.length }]);
      return;
    }
    if (diagramMode === 'strings') {
      if (pendingShape) return;
      if (draftPoints.length >= 3) {
        const first = draftPoints[0];
        const dist = Math.sqrt((x - first.x) ** 2 + (y - first.y) ** 2);
        if (dist < 4) { setPendingShape([...draftPoints]); setDraftPoints([]); setMousePos(null); return; }
      }
      setDraftPoints((prev) => [...prev, { x, y }]);
    }
  };

  const handleCanvasMouseMove = (e) => {
    if (diagramMode !== 'strings' || draftPoints.length === 0 || pendingShape || !canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    setMousePos({ x: (e.clientX - rect.left) / rect.width * 100, y: (e.clientY - rect.top) / rect.height * 100 });
  };

  const finishShape = () => { if (draftPoints.length < 3) return; setPendingShape([...draftPoints]); setDraftPoints([]); setMousePos(null); };
  const cancelDraft = () => { setDraftPoints([]); setMousePos(null); };
  const cancelPendingShape = () => { setPendingShape(null); setPendingLabel(''); setPendingColor(STRING_COLORS[0]); };

  // ── Strings ───────────────────────────────────────────────────────
  const saveString = async () => {
    if (!pendingLabel.trim() || !pendingShape || !diagram) return;
    setSavingString(true);
    try {
      const res = await axios.post(`${BASE}/commercial-sld/strings`, {
        diagram_id: diagram.id, label: pendingLabel.trim(), color: pendingColor, points: pendingShape,
      }, authHdr());
      setStrings((prev) => [...prev, res.data.string]);
      cancelPendingShape();
    } catch (e) {
      alert('Failed to save string');
    } finally {
      setSavingString(false);
    }
  };
  const deleteString = async (id) => {
    if (!window.confirm('Delete this string?')) return;
    try {
      await axios.delete(`${BASE}/commercial-sld/strings/${id}`, authHdr());
      setStrings((prev) => prev.filter((s) => s.id !== id));
    } catch (e) { alert('Failed to delete string'); }
  };

  // ── Pins ──────────────────────────────────────────────────────────
  const removePin = (localId, e) => { e.stopPropagation(); setPins((prev) => prev.filter((p) => p._localId !== localId)); };
  const updatePin = (localId, value) => { setPins((prev) => prev.map((p) => (p._localId === localId ? { ...p, label: value } : p))); };
  const savePins = async () => {
    if (!diagram) return;
    if (pins.some((p) => !String(p.label || '').trim())) { alert('All pins must have a label'); return; }
    setSavingPins(true);
    try {
      const res = await axios.put(`${BASE}/commercial-sld/diagrams/${diagram.id}/points`, {
        points: pins.map((p, i) => ({ label: p.label, x_percent: Number(p.x_percent), y_percent: Number(p.y_percent), order_index: i })),
      }, authHdr());
      setPins((res.data.points || []).map((p, i) => ({ ...p, _localId: p.id ?? `n${i}` })));
      alert('Pins saved successfully!');
    } catch (e) { alert('Failed to save pins'); }
    finally { setSavingPins(false); }
  };

  // ── Diagram upload / delete ───────────────────────────────────────
  const handleDiagramUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedSiteId) return;
    setUploadingDiag(true);
    try {
      const fd = new FormData();
      fd.append('diagram', file);
      fd.append('site_id', selectedSiteId);
      fd.append('title', selectedSite?.site_name || '');
      const res = await axios.post(`${BASE}/commercial-sld/diagrams`, fd, multipartHdr());
      setDiagram(res.data.diagram);
      setPins([]); setStrings([]); resetDraft();
    } catch (e2) {
      alert('Failed to upload diagram');
    } finally {
      setUploadingDiag(false);
      if (diagramInputRef.current) diagramInputRef.current.value = '';
    }
  };
  
  const handleDeleteDiagram = async () => {
    if (!diagram || !window.confirm('Are you sure you want to remove this diagram? ALL its pins and strings will be permanently deleted.')) return;
    try {
      await axios.delete(`${BASE}/commercial-sld/diagrams/${diagram.id}`, authHdr());
      setDiagram(null); setPins([]); setStrings([]); resetDraft();
    } catch (e) { alert('Failed to delete diagram'); }
  };

  const nearFirstPoint = draftPoints.length >= 3 && mousePos
    ? Math.sqrt((mousePos.x - draftPoints[0].x) ** 2 + (mousePos.y - draftPoints[0].y) ** 2) < 4
    : false;

  return (
    <div className="csld-wrapper">
      <style>{STYLES}</style>

      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{ marginBottom: 28 }}>
          <div className="md-page-title">Site Diagram Editor (SLD)</div>
          <div className="md-page-sub">Author diagrams, place pins & draw strings for commercial sites to guide field operations.</div>
        </div>

        {/* Pickers */}
        <div className="csld-card">
          <div className="csld-pickers">
            <div className="csld-field">
              <span className="csld-label">1. Select Client</span>
              <select className="csld-select" value={selectedClientId} onChange={(e) => setSelectedClientId(e.target.value)}>
                <option value="">Select client…</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.client_name}</option>)}
              </select>
            </div>
            <div className="csld-field">
              <span className="csld-label">2. Select Site</span>
              <select className="csld-select" value={selectedSiteId} onChange={(e) => setSelectedSiteId(e.target.value)} disabled={!selectedClientId || loadingSites}>
                <option value="">{loadingSites ? 'Loading sites…' : 'Select site…'}</option>
                {sites.map((s) => <option key={s.id} value={s.id}>{s.site_name}</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* Editor */}
        {!selectedSiteId ? (
          <div className="csld-card">
            <div className="md-empty">
              <div className="md-empty-icon"><Layers size={28} /></div>
              Please select a client and site above to manage its diagram.
            </div>
          </div>
        ) : (
          <div className="csld-card">
            {diagramLoading ? (
              <div className="md-skeleton" style={{ height: 320 }} />
            ) : !diagram ? (
              <>
                <div className="ms-diagram-upload-zone" onClick={() => diagramInputRef.current?.click()}>
                  {uploadingDiag ? (
                    <div><span className="md-spinner" style={{width: 24, height: 24, borderWidth: 3}} /><div style={{ marginTop: 16, fontSize: 14, color: 'var(--text-muted)', fontWeight: 600 }}>Uploading & Processing Diagram…</div></div>
                  ) : (
                    <>
                      <div className="ms-diagram-upload-icon"><Upload size={28} /></div>
                      <div className="ms-diagram-upload-title">Upload Site Diagram for “{selectedSite?.site_name}”</div>
                      <div className="ms-diagram-upload-sub">Supports PNG, JPG, or WEBP (Max 25MB)</div>
                    </>
                  )}
                </div>
                <input ref={diagramInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleDiagramUpload} />
              </>
            ) : (
              <>
                {/* Action row */}
                <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
                  <button className="md-btn md-btn-ghost" onClick={() => diagramInputRef.current?.click()}><Upload size={14} /> Replace Diagram</button>
                  <button className="md-btn md-btn-danger" onClick={handleDeleteDiagram}><Trash2 size={14} /> Remove Diagram</button>
                  
                  {diagramMode === 'pins' && (
                    <button className="md-btn md-btn-primary" style={{ marginLeft: 'auto', padding: '10px 20px' }} onClick={savePins} disabled={savingPins}>
                      {savingPins ? <><span className="md-spinner" /> Saving Pins…</> : <><CheckCircle2 size={16} /> Save All Pins</>}
                    </button>
                  )}
                </div>
                <input ref={diagramInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleDiagramUpload} />

                {/* Mode toggle */}
                <div className="ms-mode-toggle">
                  <button className={`ms-mode-btn${diagramMode === 'pins' ? ' active-pins' : ''}`} onClick={() => { setDiagramMode('pins'); cancelDraft(); cancelPendingShape(); }}>
                    <Move size={14} /> Place Pins {pins.length > 0 && <span style={{ opacity: 0.8 }}>({pins.length})</span>}
                  </button>
                  <button className={`ms-mode-btn${diagramMode === 'strings' ? ' active-strings' : ''}`} onClick={() => setDiagramMode('strings')}>
                    <BoxSelect size={14} /> Draw Strings {strings.length > 0 && <span style={{ opacity: 0.8 }}>({strings.length})</span>}
                  </button>
                </div>

                {/* Hint */}
                <div className="ms-canvas-hint">
                  {diagramMode === 'pins' && <><Move size={14} /> Click anywhere on the diagram to place a verification pin.</>}
                  {diagramMode === 'strings' && draftPoints.length === 0 && !pendingShape && <><BoxSelect size={14} /> Click on the diagram to start placing polygon points for a new string.</>}
                  {diagramMode === 'strings' && draftPoints.length > 0 && !pendingShape && <><BoxSelect size={14} /> {draftPoints.length} point{draftPoints.length > 1 ? 's' : ''} placed — {draftPoints.length >= 3 ? 'click the green starting dot or "Finish Shape" to close the polygon.' : `need ${3 - draftPoints.length} more point(s).`}</>}
                  {diagramMode === 'strings' && pendingShape && <>Polygon closed. Please assign a label and color below to save.</>}
                </div>

                {/* Draft toolbar */}
                {diagramMode === 'strings' && draftPoints.length > 0 && !pendingShape && (
                  <div className="ms-draft-toolbar">
                    <span style={{ flex: 1 }}>{draftPoints.length} point{draftPoints.length !== 1 ? 's' : ''} placed</span>
                    {draftPoints.length >= 3 && <button className="md-btn md-btn-primary" style={{ padding: '6px 14px' }} onClick={finishShape}><CheckCircle2 size={14} /> Finish Shape</button>}
                    <button className="md-btn md-btn-ghost" style={{ padding: '6px 14px', background: 'var(--bg-card)', borderColor: 'var(--border-main)' }} onClick={cancelDraft}><X size={14} /> Cancel Draft</button>
                  </div>
                )}

                {/* Canvas */}
                <div className="ms-pin-canvas-wrap" ref={canvasRef} onClick={handleCanvasClick} onMouseMove={handleCanvasMouseMove} onMouseLeave={() => setMousePos(null)}>
                  <img src={`${IMG_BASE}${diagram.diagram_url}`} alt="Site diagram" className="ms-pin-canvas-img" draggable={false} />

                  <svg className="ms-svg-overlay" viewBox="0 0 100 100" preserveAspectRatio="none">
                    {/* Render saved strings */}
                    {strings.map((s) => {
                      const pts = Array.isArray(s.points) ? s.points : [];
                      if (pts.length < 3) return null;
                      return <polygon key={s.id} points={svgPts(pts)} fill={`${s.color}30`} stroke={s.color} strokeWidth="0.4" strokeOpacity="0.9" />;
                    })}
                    
                    {/* Render draft lines */}
                    {draftPoints.length >= 2 && <polyline points={svgPts(draftPoints)} fill="none" stroke="var(--accent-blue)" strokeWidth="0.5" strokeDasharray="1.5,1" />}
                    {draftPoints.length > 0 && mousePos && (
                      <line x1={draftPoints[draftPoints.length - 1].x} y1={draftPoints[draftPoints.length - 1].y} x2={mousePos.x} y2={mousePos.y} stroke="var(--accent-blue)" strokeWidth="0.4" strokeDasharray="1,1" opacity="0.7" />
                    )}
                    
                    {/* Render draft points */}
                    {draftPoints.map((p, i) => (
                      <circle key={i} cx={p.x} cy={p.y} r={i === 0 ? '1.5' : '0.8'} fill={i === 0 ? (nearFirstPoint ? '#10B981' : 'var(--text-strong)') : 'var(--bg-card)'} stroke={i === 0 ? '#10B981' : 'var(--accent-blue)'} strokeWidth={i === 0 ? '0.5' : '0.2'} />
                    ))}
                    {nearFirstPoint && <circle cx={draftPoints[0].x} cy={draftPoints[0].y} r="3" fill="none" stroke="#10B981" strokeWidth="0.5" opacity="0.8" />}
                  </svg>

                  {/* Render string labels */}
                  {strings.map((s) => {
                    const pts = Array.isArray(s.points) ? s.points : [];
                    if (pts.length < 3) return null;
                    const c = centroid(pts);
                    return <span key={`lbl-${s.id}`} className="ms-string-label-chip" style={{ left: `${c.x}%`, top: `${c.y}%`, color: s.color }}>{s.label}</span>;
                  })}

                  {/* Render pins */}
                  {pins.map((pin, idx) => (
                    <div key={pin._localId} className="ms-pin" style={{ left: `${pin.x_percent}%`, top: `${pin.y_percent}%` }} onClick={(e) => e.stopPropagation()}>
                      <div className="ms-pin-dot">{idx + 1}</div>
                      <div className="ms-pin-tooltip">{pin.label || `Pin ${idx + 1}`}</div>
                      {diagramMode === 'pins' && <button className="ms-pin-remove" onClick={(e) => removePin(pin._localId, e)}><X size={10} strokeWidth={3} /></button>}
                    </div>
                  ))}
                </div>

                {/* Pending string label form */}
                {diagramMode === 'strings' && pendingShape && (
                  <div className="ms-pending-form">
                    <div className="ms-pending-form-title">Save New String ({pendingShape.length} Points)</div>
                    <input className="csld-input" placeholder='Enter Label (e.g. "String 1", "South Array")' value={pendingLabel} onChange={(e) => setPendingLabel(e.target.value)} style={{ marginBottom: 16 }} autoFocus onKeyDown={(e) => { if (e.key === 'Enter') saveString(); if (e.key === 'Escape') cancelPendingShape(); }} />
                    <div className="ms-color-row">
                      <span style={{ fontSize: 11, fontWeight: 700, fontFamily: 'DM Mono', color: 'var(--text-muted)', letterSpacing: '0.1em', textTransform: 'uppercase', flexShrink: 0, marginRight: 8 }}>Select Color</span>
                      {STRING_COLORS.map((c) => <div key={c} className={`ms-color-swatch${pendingColor === c ? ' selected' : ''}`} style={{ background: c }} onClick={() => setPendingColor(c)} />)}
                    </div>
                    <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
                      <button className="md-btn md-btn-ghost" style={{ background: 'var(--bg-card)' }} onClick={cancelPendingShape}>Cancel</button>
                      <button className="md-btn md-btn-primary" onClick={saveString} disabled={savingString || !pendingLabel.trim()}>
                        {savingString ? <><span className="md-spinner" /> Saving…</> : <><CheckCircle2 size={14} /> Save String</>}
                      </button>
                    </div>
                  </div>
                )}

                {/* Strings list */}
                {diagramMode === 'strings' && strings.length > 0 && (
                  <>
                    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text-muted)', fontFamily: 'DM Mono', marginTop: 28, marginBottom: 12 }}>Defined Strings ({strings.length})</div>
                    <div className="ms-string-list">
                      {strings.map((s) => (
                        <div key={s.id} className="ms-string-item">
                          <div className="ms-string-color-block" style={{ background: s.color }} />
                          <span style={{ flex: 1, fontSize: 14, fontWeight: 700, color: 'var(--text-strong)' }}>{s.label}</span>
                          <span style={{ fontSize: 11, fontWeight: 600, fontFamily: 'DM Mono', color: 'var(--text-muted)' }}>{Array.isArray(s.points) ? `${s.points.length} pts` : ''}</span>
                          <button className="md-btn md-btn-danger" style={{ padding: '6px 10px' }} onClick={() => deleteString(s.id)}><Trash2 size={14} /></button>
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {/* Pins list */}
                {diagramMode === 'pins' && pins.length > 0 && (
                  <>
                    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text-muted)', fontFamily: 'DM Mono', marginTop: 28, marginBottom: 12 }}>Pin Labels ({pins.length})</div>
                    <div className="ms-pin-list">
                      {pins.map((pin, idx) => (
                        <div key={pin._localId} className="ms-pin-list-item">
                          <div className="ms-pin-list-num">{idx + 1}</div>
                          <input className="ms-pin-label-input" placeholder={`Enter label for Pin ${idx + 1}`} value={pin.label || ''} onChange={(e) => updatePin(pin._localId, e.target.value)} />
                          <button className="md-btn md-btn-danger" style={{ padding: '6px 10px' }} onClick={(e) => removePin(pin._localId, e)}><X size={14} /></button>
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {diagramMode === 'pins' && pins.length === 0 && (
                  <div className="md-empty" style={{ padding: '32px 0', border: '1px dashed var(--border-main)', borderRadius: '12px', marginTop: 20 }}>
                    <div className="md-empty-icon"><MapPin size={24} /></div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-strong)', marginBottom: 4 }}>No pins placed yet</div>
                    <div style={{ fontSize: 13 }}>Click anywhere on the diagram above to add verification pins.</div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CISLDEditor;